import {useCallback, useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';

import { getLocal, getPath, getPathExists, TIPS, TIPS_INTERVAL } from '../../utils/api';

import Header from '../header/Header';

import './Path.css';

function Path({ handleError, handleFeedback, handlePath, handleStartOver }) {
  const ACTIVITY_CLASSES = ['center', 'left', 'center', 'right'];
  const navigate = useNavigate();

  const [activities, setActivities] = useState(null);
  const [directions, setDirections] = useState(null);
  const [future, setFuture] = useState(null);
  const [geography, setGeography] = useState(null);
  const [profile, ] = useState(getLocal('profile'));
  const [showSummary, setShowSummary] = useState(null);
  const [strengths, setStrengths] = useState(null);
  const [tipIndex, setTipIndex] = useState(null);
  const [values, setValues] = useState(null);

  const getActivities = useCallback(async () => {
    try {
      setShowSummary(!(await getPathExists()));
      setActivities(await getPath());
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  const handleActivityById = useCallback((id) => {
    navigate('/activity/' + id);
  }, [navigate]);

  const handleActivityByType = useCallback((type) => {
    const activity = activities?.find(activity => activity.type === type);

    if (activity) {
      navigate('/activity/' + activity._id);
    }
  }, [activities, navigate]);

  const handleChat = useCallback(() => {
    navigate('/chat');
  }, [navigate]);

  useEffect(() => {
    getActivities();
  }, [getActivities]);

  useEffect(() => {
    if (profile?.meta_data) {
      const direction = profile.meta_data.find(item => item.param === 'direction' && item.value);

      if (direction) {
        setDirections(direction.value);
      }

      const future = profile.meta_data.find(item => item.param === 'future' && item.value);

      if (future) {
        setFuture(future.value);
      }

      const values = profile.meta_data.find(item => item.param === 'values' && item.value);

      if (values) {
        setValues(values.value);
      }

      const strengths = profile.meta_data.find(item => item.param === 'strengths' && item.value);

      if (strengths) {
        setStrengths(strengths.value);
      }

      const geography = profile.meta_data.find(item => item.param === 'geography' && item.value);

      if (geography) {
        setGeography(geography.value);
      }
    }
  }, [profile]);

  useEffect(() => {
    if (TIPS?.length) {
      setTipIndex(0);

      const tipInterval = setInterval(() => {
        setTipIndex(prevTipIndex => (prevTipIndex >= TIPS.length - 1 ? 0 : prevTipIndex + 1));
      }, TIPS_INTERVAL);

      return () => clearInterval(tipInterval);
    }
  }, []);

  if (showSummary === null) {
    return null;
  }

  return (
    <>
      {!showSummary ? (
        <Header activeElement="path" handleFeedback={handleFeedback} handlePath={handlePath} handleStartOver={handleStartOver} />
      ) : null}
      <div id="path" className={'overflow' + (showSummary ? ' loading' : '')}>
        <div className="clamp">
          {showSummary ? (
            <div className="path-loading-container">
              <div className="summary">
                <div className="summary-title">Here's what I know about you so far.</div>
                {directions?.length ? (
                  <div className="summary-section">
                    <div className="summary-section-title">Direction After High School</div>
                    <div className="pills">
                      {directions.map((direction, directionIndex) => 
                        <div key={directionIndex} className="pill pill-theme-2">{direction}</div>
                      )}
                    </div>
                  </div>
                ) : null}
                {future ? (
                  <div className="summary-section">
                    <div className="summary-section-title">What You’re Thinking About</div>
                    <div className="summary-section-value">"{future}"</div>
                  </div>
                ) : null}
                {values ? (
                  <div className="summary-section">
                    <div className="summary-section-title">What Matters Most to Me</div>
                    <div className="pills">
                      <div className="pill pill-theme-2">{values}</div>
                    </div>
                  </div>
                ) : null}
                {strengths?.length ? (
                  <div className="summary-section">
                    <div className="summary-section-title">What Comes Naturally to Me</div>
                    <div className="pills">
                      {strengths.map((strength, strengthIndex) => 
                        <div key={strengthIndex} className="pill pill-theme-2">{strength}</div>
                      )}
                    </div>
                  </div>
                ) : null}
                {geography ? (
                  <div className="summary-section">
                    <div className="summary-section-title">How Far from Home</div>
                    <div className="pills">
                      <div className="pill pill-theme-2">{geography}</div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="financial-aid">
                {tipIndex !== null ? (
                  <>
                    <div className="financial-aid-title">Did You Know?</div>
                    <div className="financial-aid-tip">{TIPS[tipIndex]}</div>
                  </>
                ) : null}
                {activities !== null ? (
                  <div className="action" onClick={() => handleActivityByType('financial')}>Find financial aid now →</div>
                ) : null}
              </div>
              {activities === null ? (
                <>
                  <div className="path-loading-message">Your AI Guide is getting ready...</div>
                  <div className="path-progress-container">
                    <div className="path-progress"></div>
                  </div>
                </>
              ) : (
                <div className="action" onClick={() => handleActivityByType('start')}>Take me to my Guide →</div>
              )}
            </div>
          ) : (
            <>
              <div className="section-title">Your Path</div>
              {activities !== null ? (
                <>
                  <div className="callout">Start on any step!</div>
                  <div className="pathway">
                    {activities?.length ? activities.map((activity, activityIndex) => {
                      return (
                        <div key={activityIndex} className={'button activity ' + ACTIVITY_CLASSES[activityIndex % ACTIVITY_CLASSES.length] + ' ' + (activity.type || 'career') + (activity.status === 'in_progress' ? ' active' : '') + (activity.status === 'completed' ? ' completed' : '')} onClick={() => handleActivityById(activity._id)}>
                          <div className="button-inner">
                            <div className="button-title">{activity.title}</div>
                          </div>
                        </div>
                      );
                    }) : null}
                  </div>
                  <div className="callout">Start Your Career!</div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
      {!showSummary ? (
        <div id="chat-button" onClick={handleChat}></div>
      ) : null}
    </>
  );
}

export default Path;
